import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    DestroyRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { defaultFormChangeDebounceTime } from '@hq-core/models/forms';
import { TextFilterChangeEvent, TextFilterSearchTypeChangeEvent } from '@cia-front-end-apps/shared/api-interaction';
import { debounceTime, filter, map } from 'rxjs/operators';
import { DropdownOption } from '@hq-shared/models/dropdown-option';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'hq-text-filter',
    templateUrl: './text-filter.component.html',
    styleUrls: ['./text-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TextFilterComponent implements OnInit, OnChanges {
    private destroyRef = inject(DestroyRef);

    @Input() isDisabled: boolean;
    @Input() placeholder: string;
    @Input() textOverride: string;

    @Input() searchTypeOptions: Array<DropdownOption>;
    @Input() searchTypeOverride: string;

    @Output() changeFilter = new EventEmitter<TextFilterChangeEvent>()
    @Output() changeSearchType = new EventEmitter<TextFilterSearchTypeChangeEvent>();


    readonly filterControl = new FormControl('');
    readonly searchTypeControl = new FormControl<string | null>(null);

    private currentText: string;
    private currentSearchType: string | number | null = null;
    private readonly defaultText = '';

    ngOnInit(): void {
        this.currentText = this.defaultText;

        this.filterControl.valueChanges
            .pipe(
                debounceTime(defaultFormChangeDebounceTime),
                map((input: string) => {
                    return input.trim();
                }),
                filter((input: string) => input !== this.currentText),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((input: string) => {
                this.currentText = input;
                const event = new TextFilterChangeEvent({
                    text: input
                });
                this.changeFilter.emit(event);
            });

        this.searchTypeControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                const event = new TextFilterSearchTypeChangeEvent({
                    searchType: value
                });
                this.changeSearchType.emit(event);
            });

        if (this.isDisabled) {
            this.filterControl.disable();
            this.searchTypeControl.disable();
        }

        if (this.textOverride) {
            this.applyTextOverride(this.textOverride);
        }

        if (this.searchTypeOverride) {
            this.applySearchTypeOverride(this.searchTypeOverride);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isDisabled && !changes.isDisabled.firstChange) {
            this.filterControl.enable();
            this.searchTypeControl.enable();

            if (this.isDisabled) {
                this.filterControl.disable();
                this.searchTypeControl.disable();
            }
        }

        if (changes.textOverride && !changes.textOverride.firstChange) {
            this.applyTextOverride(this.textOverride);
        }

        if (changes.searchTypeOverride && !changes.searchTypeOverride.firstChange) {
            this.applySearchTypeOverride(this.searchTypeOverride);
        }
    }

    onClearFilter(): void {
        this.filterControl.setValue(this.defaultText);
    }

    /** @description Applies a text override without triggering an event. Overrides are ignored if
     * they already match the current text value
     * @param overrideText The text to set as the filter's value
     */
    private applyTextOverride(overrideText: string): void {
        const textOverride = overrideText || this.defaultText;
        if (textOverride !== this.currentText) {
            this.filterControl.setValue(textOverride, { emitEvent: false });
            this.currentText = textOverride;
        }
    }

    private applySearchTypeOverride(overrideType: string): void {
        const typeOverride = overrideType || null;
        if (typeOverride !== this.currentSearchType) {
            this.searchTypeControl.setValue(typeOverride, { emitEvent: false });
            this.currentSearchType = typeOverride;
        }
    }
}
